import { IRegion } from '@/creditflowresearch/domain/vo/Region';
import { RegionType, RegionAbbreviations } from './RegionType';
import {
  RegionIdToTypeMap,
  RegionAbbreviationToTypeMap,
  RegionAbbreviationById,
  RegionIdByAbbreviation,
  RegionIdByEmAbbreviation,
  LATM_REGION_ID,
  EMEA_REGION_ID,
  APAC_REGION_ID,
  USOA_REGION_ID,
} from './constants';
import BaseDomainEntity from '@/creditflowresearch/domain/BaseDomainEntity';
import Market from '@/creditflowresearch/domain/Market';

abstract class Region extends BaseDomainEntity<Region, IRegion> {
  static idRegistry = new Map<string, Region>();

  static readonly USOA_ID: string = USOA_REGION_ID;

  static readonly APAC_ID: string = APAC_REGION_ID;

  static readonly EMEA_ID: string = EMEA_REGION_ID;

  static readonly LATM_ID: string = LATM_REGION_ID;

  static readonly USOA_ABBREVIATION: RegionAbbreviations = RegionAbbreviations.USOA;

  static readonly APAC_ABBREVIATION: RegionAbbreviations = RegionAbbreviations.APAC;

  static readonly EMEA_ABBREVIATION: RegionAbbreviations = RegionAbbreviations.EMEA;

  static readonly LATM_ABBREVIATION: RegionAbbreviations = RegionAbbreviations.LATM;

  protected readonly abbreviation: RegionAbbreviations;

  protected readonly name: string;

  protected readonly displayName: string;

  protected readonly emDisplayName: string;

  protected readonly type: RegionType;

  constructor(regionData: IRegion) {
    super(regionData);
    this.validateRegionData(regionData);
    this.abbreviation = regionData.abbreviation as RegionAbbreviations;
    this.name = regionData.name;
    this.displayName = regionData.displayName;
    this.emDisplayName = regionData.emDisplayName;
    this.type = this.determineType(regionData.id);
  }

  canAccess(accessibleRegionIds: string[] | Set<string>): boolean {
    if (accessibleRegionIds instanceof Set) {
      return accessibleRegionIds.has(this.id);
    }
    return accessibleRegionIds.includes(this.id);
  }

  getAbbreviation(): RegionAbbreviations {
    return this.abbreviation;
  }

  getName(): string {
    return this.name;
  }

  getDisplayName(): string {
    return this.displayName;
  }

  getEmDisplayName(): string {
    return this.emDisplayName;
  }

  getType(): RegionType {
    return this.type;
  }

  isOfType(type: RegionType): boolean {
    return this.type === type;
  }

  belongsToMarket(): Market | null {
    const marketId = this.getMarketIdForRegion();
    if (!marketId) {
      return null;
    }

    return Market.from(marketId);
  }

  protected abstract getMarketIdForRegion(): string | null;

  private validateRegionData(regionData: IRegion): void {
    if (!regionData?.id) {
      throw new Error('Region data must have an id');
    }
    if (!regionData?.abbreviation) {
      throw new Error('Region data must have an abbreviation');
    }
    if (!regionData?.name) {
      throw new Error('Region data must have a name');
    }
  }

  private determineType(regionId: string): RegionType {
    const type = RegionIdToTypeMap[regionId];
    if (!type) {
      throw new Error(`Unknown region ID: ${ regionId }`);
    }
    return type;
  }

  static getDefaultId(): string {
    return Region.USOA_ID;
  }

  static getDefaultAbbreviation(): RegionAbbreviations {
    return RegionAbbreviations.USOA;
  }

  static getAbbreviation(regionId: string): RegionAbbreviations | null {
    const abbreviation = RegionAbbreviationById[regionId];
    if (!abbreviation) {
      return null;
    }
    return abbreviation as RegionAbbreviations;
  }

  static getId(abbreviation: string): string | null {
    return RegionIdByAbbreviation[abbreviation]
           || RegionIdByEmAbbreviation[abbreviation]
           || null;
  }

  static isOfType(identifier: string, type: RegionType): boolean {
    const typeById = RegionIdToTypeMap[identifier];
    const typeByAbbreviation = RegionAbbreviationToTypeMap[identifier];

    return typeById === type || typeByAbbreviation === type;
  }

  static areAllOfType(identifiers: string[], type: RegionType): boolean {
    if (!identifiers?.length) {
      return false;
    }

    return identifiers.every((identifier) => Region.isOfType(identifier, type));
  }

  static areAllOfTypes(identifiers: string[], types: RegionType[]): boolean {
    if (!identifiers?.length || !types?.length) {
      return false;
    }

    return identifiers.every((identifier) =>
      types.some((type) => Region.isOfType(identifier, type)),
    );
  }

  static hasAnyOfType(identifiers: string[], type: RegionType): boolean {
    if (!identifiers?.length || !type) {
      return false;
    }

    return identifiers.some((identifier) => Region.isOfType(identifier, type));
  }

  static containAllTypes(identifiers: string[], types: RegionType[]): boolean {
    if (!identifiers?.length || !types?.length) {
      return false;
    }

    return types.every((type) => Region.hasAnyOfType(identifiers, type));
  }

  static getAllAbbreviationsString(): string {
    return Region.getAll().map((r) => r.getAbbreviation()).join(',');
  }

  static getMarketsByRegions = (identifiers: string[]) => {
    return identifiers
      .map((identifier) => {
        const region = Region.from(identifier);

        if (!region) {
          return null;
        }

        return region.belongsToMarket();
      })
      .filter(Boolean);
  };
}

export default Region;
